import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  FormLayoutContent,
  FormLayoutFooter,
  FormLayout,
} from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as distributorSelectors from 'data/distributor/distributor.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as nodesSelectors from 'data/nodes/nodes.selectors';
import * as filterGroupActions from 'data/filterGroup/filterGroup.actions';
import { FilterGroup } from 'data/filterGroup/filterGroup.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { newFilterGroup } from 'routes/FilterGroup/FilterGroup.types';
import { filterGroupTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptFilterGroupTemplate,
  adaptInitialFilterGroupFormData,
} from '../../utils/filterGroupTemplate.adapter';
import { Ref } from '../../components/FilterGroupForm/FilterGroupForm';
import FilterGroupForm from '../../components/FilterGroupForm';

type RouterParams = {
  history: string;
};
const mapState = (state: RootState) => ({
  filterGroupTemplateDef: adaptTemplateVisibilityMode(
    filterGroupTemplate,
    'create',
  ),
  userId: state.authentication.userId,
  distributors: distributorSelectors.getDistributors(state),
  nodes: nodesSelectors.getNodes(state),
  filterGroupTemplate: adaptTemplateVisibilityMode(filterGroupTemplate, 'create'),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
});

const mapDispatch = {
  createFilterGroup: filterGroupActions.createFilterGroup,
  goToFilterGroupList:
    filterGroupActions.goToFilterGroupList,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;
export type Props = PropsFromRedux & PropsFromRouter;

export const FilterGroupCreate = ({
  history,
  goToFilterGroupList,
  createFilterGroup,
  filterGroupTemplateDef,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<FilterGroup>(adaptInitialFilterGroupFormData(cloneDeep(newFilterGroup)));
  const [template, setTemplate] = useState(filterGroupTemplateDef);
  const { t } = useTranslation();

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null || formData == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as FilterGroup);
    let clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate = adaptFilterGroupTemplate(clonedTemplate);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateTLSActionPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const filterGroupFormData = formRef.current.getValues();

    const payload = {
      data: filterGroupFormData,
    };

    return payload;
  };

  const handleClickSave = () => {
    const payload = getCreateTLSActionPayload();
    if (payload) {
      createFilterGroup(payload, history);
    }
  };

  const handleCancel = () => goToFilterGroupList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <FilterGroupForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <FormLayout>
      <FormLayoutContent>
        {renderFormPanel(t('filterGroup.create.title'))}
        {renderForm()}
      </FormLayoutContent>
    </FormLayout>
  );
};

export default withRouter(connector(FilterGroupCreate));
